import { action, observable, runInAction } from 'mobx'
import moment from 'moment-timezone'
import { dateToDay, nullCleanObject } from '../../../shared-lib/utils'
import { DiaryEventWarningStatus, DiarySegmentType } from '../../../graphQL/generated/enums'
import { $RootStore } from '../../../src/stores/RootStore'
import { SSPA_AVAILABILITY_ID } from '../../../shared-lib/commonStrings'
import { isEmptyOrSSpaAgenda } from '../../../shared-solo/lib/utils'
import listeners from '../../communication/Listeners'
import { AvailabilityStatus } from '../ProfessionalAvailabilityStore/ProfessionalAvailabilityStore'

export interface $DiaryEventSegment {
  durationHours: number
  type: DiarySegmentType
  color: string
}

// The graph's event plus what the store adds to it. `warningStatus` is re-declared because the
// generated type is a string union (`enumsAsTypes: true`) while the client compares against the
// enum of the same values, and the two are not interchangeable.
export interface $DiaryEvent extends Omit<DiaryEvent, 'warningStatus'> {
  warningStatus: DiaryEventWarningStatus
  segments?: Array<$DiaryEventSegment>
  memberName?: string
}

// An agenda's availability, as this store guarantees it rather than as the schema declares it.
// `ProfessionalAvailability` is nullable throughout — it doubles as the type for standalone
// availability records, where most fields genuinely are optional — but an agenda always carries a
// populated one, and the calendar reads these four on every render.
export interface $AgendaAvailability extends ProfessionalAvailability {
  day: string
  status: number
  times: number[]
  workingTimes: number[][]
  locationId?: string
}

// An agenda as the store holds it: availability present, and every event tagged with the member it
// belongs to. Replaces the hand-written `$AgendaDayData`, which said the same thing before the
// graph types existed.
export interface $ProfessionalAgenda extends Omit<ProfessionalAgenda, 'availability' | 'events'> {
  availability: $AgendaAvailability
  events: $DiaryEvent[]
}

const emptyAvailability = (day: string): $AgendaAvailability =>
  ({ day, status: 1, times: [], workingTimes: [], locationId: undefined })

export const agendaShowsAvailability = (agenda: $ProfessionalAgenda) => {
  const { status, workingTimes, locationId } = agenda.availability
  const instantAtSecretSpa = status === AvailabilityStatus.instant && locationId === SSPA_AVAILABILITY_ID
  return (instantAtSecretSpa || status !== AvailabilityStatus.unavailable) && workingTimes.length > 0
}

class AgendaStore {
  rootStore: $RootStore
  @observable accessor loading = false
  @observable accessor agendasByday: Record<string, $ProfessionalAgenda[]> = {}
  @observable accessor cacheTimes: Record<string, number> = {}
  calendarUpdatesSubscription?: { unsubscribe: () => void }

  @action.bound clearData = () => {
    this.stopListeningOnCalendarUpdates()
    this.loading = false
    this.agendasByday = {}
    this.cacheTimes = {}
  }

  // Listen for calendar updates on other devices for real-time updates
  @action.bound listenOnCalendarUpdates_api(memberIds: string[]) {
    const { communication } = this.rootStore

    this.stopListeningOnCalendarUpdates()

    this.calendarUpdatesSubscription = communication.client.subscribe<Subscription>({
      query: listeners.calendarUpdated,
      variables: { memberIds },
    }).subscribe({
      next: res => {
        const { dates = [], weekdays = [] } = res.data?.calendarUpdated ?? {}
        const days = new Set(dates.map(date => dateToDay(new Date(date))))

        // Default hours and repeating bookings change every loaded day on that weekday
        Object.keys(this.cacheTimes)
          .map(this.dayFromAgendaKey)
          .filter(day => weekdays.includes(moment(day).day()))
          .forEach(day => days.add(day))

        if (days.size > 0) this.resetCache([...days])
      },
      // Logged rather than surfaced: a dropped socket already raises the booking subscription's
      // message, and a second one for the same outage would only be noise.
      error: (err: unknown) => console.error(err),
    })
  }

  @action.bound stopListeningOnCalendarUpdates() {
    this.calendarUpdatesSubscription?.unsubscribe()
    this.calendarUpdatesSubscription = undefined
  }

  constructor(rootStore: $RootStore) {
    this.rootStore = rootStore
  }

  @action.bound resetCache = (days?: Array<string>) => {
    if (!days) {
      this.cacheTimes = {}
      return
    }

    const daysToReset = new Set(days)

    Object.keys(this.cacheTimes).forEach(key => {
      if (daysToReset.has(this.dayFromAgendaKey(key))) {
        this.cacheTimes[key] = 0
      }
    })

    days.forEach(day => {
      this.cacheTimes[day] = 0
    })
  }

  @action.bound filterRecentlyFetchedDays = (days: Array<string>, daysToFetch: Array<string>, memberId?: string) => {
    const cacheTime = 30 * 1000
    const now = Date.now()

    days.forEach((date: string) => {
      const key = this.agendaKey(date, memberId)
      const lastFetchedAt = this.cacheTimes[key]

      if (!lastFetchedAt || (now - cacheTime) > lastFetchedAt) {
        daysToFetch.push(date)
        this.cacheTimes[key] = now
      }
    })
  }

  fetchAgendas_api = async (daysRequested: Array<string> = [], memberId?: string, memberName?: string) => {
    const { communication: { requester } } = this.rootStore

    const daysToFetch: Array<string> = []

    this.filterRecentlyFetchedDays(daysRequested, daysToFetch, memberId)

    // Don't send API request if nothing to fetch
    if (daysToFetch.length === 0) return

    runInAction(() => {
      this.loading = true
    })

    try {
      const response = await requester.getProfessionalAgendas({
        days: daysToFetch,
        memberId,
      })

      const agendas = response.getProfessionalAgendas || []

      const fetched: Record<string, $ProfessionalAgenda[]> = Object.fromEntries(
        daysToFetch.map(day => [this.agendaKey(day, memberId), []]),
      )

      agendas.forEach(agenda => {
        const cleaned: $ProfessionalAgenda | null = nullCleanObject(agenda)
        if (!cleaned) return

        const key = this.agendaKey(cleaned.day, memberId)
        const stored: $ProfessionalAgenda = {
          ...cleaned,
          availability: cleaned.availability ?? emptyAvailability(cleaned.day),
        }

        fetched[key] ??= []
        fetched[key].push(memberName
          ? { ...stored, events: stored.events.map(event => ({ ...event, memberName })) }
          : stored)
      })

      runInAction(() => {
        for (const [key, dayAgendas] of Object.entries(fetched)) {
          this.agendasByday[key] = dayAgendas
        }
      })
    } catch (error) {
      runInAction(() => {
        daysToFetch.forEach(day => delete this.cacheTimes[this.agendaKey(day, memberId)])
      })
      throw error
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  agendaKey = (day: string, memberId?: string) => {
    return memberId ? `${memberId}:${day}` : day
  }

  dayFromAgendaKey = (key: string) => {
    const separatorIndex = key.indexOf(':')
    return separatorIndex === -1 ? key : key.slice(separatorIndex + 1)
  }

  getDefaultOrSSpaAgenda = (day: string, memberId?: string) => {
    const { authStore: { isSaasUser } } = this.rootStore
    const agendas = this.agendasByday[this.agendaKey(day, memberId)]

    if (!isSaasUser && agendas) {
      const agenda = agendas.find(agenda => isEmptyOrSSpaAgenda(agenda.availability.locationId))
      if (agenda) {
        return agenda
      }
    }

    return this.createDefaultAvailability(day)
  }

  getAgendaStatus = (day: string, memberId?: string) => {
    const { authStore: { isSaasUser } } = this.rootStore

    if (!isSaasUser) {
      const agenda = this.getDefaultOrSSpaAgenda(day, memberId)
      return agenda.availability.status
    }

    return 1
  }

  getEvents = (day: string, memberId?: string): $DiaryEvent[] => {
    const agendas = this.agendasByday[this.agendaKey(day, memberId)]

    if (!agendas || agendas.length === 0) {
      return []
    }

    const events: $DiaryEvent[] = []

    agendas.forEach(agenda => {
      agenda.events.forEach(event => {
        const existingEvent = events.find(e => e.bookingId === event.bookingId)
        if (!existingEvent) {
          events.push(event)
        }
      })
    })

    return events ?? []
  }

  getEnabledAgendas = (day: string, memberId?: string) => {
    const agendas = this.agendasByday[this.agendaKey(day, memberId)]

    const { userStore: { validLocations }, authStore: { isSalonSession } } = this.rootStore

    // Colleague hours that aren't at one of our locations (own business, SecretSpa) stay hidden.
    const viewingColleague = !isSalonSession && !!memberId

    const enabledAgendas = agendas?.filter(agenda => {
      const { locationId } = agenda.availability

      if (isEmptyOrSSpaAgenda(locationId)) return !viewingColleague

      return !!validLocations.find(location =>
        location?._id === locationId && location?.enabled,
      )
    })

    if (!enabledAgendas || enabledAgendas.length === 0) {
      return [this.createDefaultAvailability(day)]
    }

    const agendaOrder = [
      SSPA_AVAILABILITY_ID,
      ...validLocations.map(location => location!._id),
    ]

    enabledAgendas.sort(({ availability: a }, { availability: b }) =>
      agendaOrder.findIndex(id => id === a.locationId) - agendaOrder.findIndex(id => id === b.locationId),
    )

    return enabledAgendas
  }

  hasCustomSchedule = (day: string, memberId?: string) =>
    this.getEnabledAgendas(day, memberId).some(agenda => agenda.isCustom)

  hasAgendaForDay = (day: string, memberId?: string) =>
    this.agendasByday[this.agendaKey(day, memberId)] !== undefined

  isAvailableOnDay = (day: string, memberId?: string) =>
    this.getEnabledAgendas(day, memberId).some(agendaShowsAvailability)

  bookingCountOnDay = (day: string, memberId?: string) =>
    this.getEvents(day, memberId).filter(event => event.type === 'external').length

  createDefaultAvailability = (day: string): $ProfessionalAgenda => ({
    day,
    events: [],
    availability: emptyAvailability(day),
    updatedAt: new Date(),
    isCustom: false,
  })

  get totalAgendaEvents() {
    return Object.values(this.agendasByday)
      .map(a => a.map(b => b.events.filter(e => e.type === 'external')))
      .flat()
      .flat()
      .length
  }
}

export default AgendaStore
